import { LogOut, Mail, Phone, School, Trash2, Trophy } from 'lucide-react-native';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState } from 'react';
import { Linking, Platform, Pressable, StyleSheet, View } from 'react-native';

import { Avatar } from '@/components/Avatar';
import { Button } from '@/components/Button';
import { DeleteAccountSheet } from '@/components/DeleteAccountSheet';
import { EditEmailSheet } from '@/components/EditEmailSheet';
import { EditNameSheet } from '@/components/EditNameSheet';
import { Heading } from '@/components/Heading';
import { NotificationSetting } from '@/components/NotificationSetting';
import { Overline } from '@/components/Overline';
import { Pill } from '@/components/Pill';
import { ProfileStatsCard } from '@/components/ProfileStatsCard';
import { Screen } from '@/components/Screen';
import { Text } from '@/components/Text';
import { LINKS, SUPPORT_EMAIL } from '@/constants/api';
import { colors, spacing } from '@/constants/theme';
import { openEmail } from '@/lib/openEmail';
import { signOutThisDevice } from '@/lib/signOut';
import { usersService } from '@/services/users';
import { authStore } from '@/stores/auth.store';
import { referralStatsStore } from '@/stores/referralStats.store';
import type { MeStats } from '@/types/earning';

const ProfileTab = observer(() => {
  const [stats, setStats] = useState<MeStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(false);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editEmailOpen, setEditEmailOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);


  const user = authStore.user;

  const fetchStats = useCallback(async () => {
    setStatsError(false);
    setStatsLoading(true);
    try {
      const data = await usersService.stats();
      setStats(data);
    } catch {
      setStatsError(true);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchStats();
    // Lazy-load referral stats so the Referrals tile resolves even for
    // users who hit Profile before Refer in a given session.
    void referralStatsStore.ensureLoaded();
  }, [fetchStats]);

  const initials = user
    ? `${user.first_name?.charAt(0) ?? ''}${user.last_name?.charAt(0) ?? ''}`
    : '?';

  const fullName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Athlete'
    : 'Athlete';



  return (
    <Screen testID="profile-screen">
      <View style={styles.header}>
        <Avatar initials={initials} size={72} />
        <Pressable onPress={() => setEditNameOpen(true)} accessibilityRole="button">
          <Heading size="3xl">{fullName}</Heading>
        </Pressable>
        {user?.sport && <Pill tone="greenSoft" icon={Trophy}>{user.sport}</Pill>}
      </View>

      <ProfileStatsCard
        stats={stats}
        loading={statsLoading}
        error={statsError}
        onRetry={fetchStats}
        referralCount={referralStatsStore.data?.totals.conversions ?? null}
      />

      <View style={styles.section}>
        <Overline>Profile</Overline>
        <View style={styles.infoRows}>
          <Pressable style={styles.infoRow} onPress={() => setEditNameOpen(true)} accessibilityRole="button" accessibilityLabel="Edit name">
            <Text variant="bodyMedium">{fullName}</Text>
            <Text variant="caption" color="textMuted">Tap to edit</Text>
          </Pressable>
          {user?.email && (
            <Pressable style={styles.infoRow} onPress={() => setEditEmailOpen(true)} accessibilityRole="button" accessibilityLabel={`Email, ${user.email}`} accessibilityHint="Opens a sheet to change your email" testID="edit-email-row">
              <View style={[styles.infoRowIcon, styles.infoRowValue]}>
                <Mail size={16} color={colors.textMuted} />
                <Text variant="body" numberOfLines={1} style={styles.infoRowText}>{user.email}</Text>
              </View>
              <Text variant="caption" color="textMuted">Tap to edit</Text>
            </Pressable>
          )}
          {user?.phone_e164 && (
            <View style={styles.infoRow}>
              <View style={styles.infoRowIcon}>
                <Phone size={16} color={colors.textMuted} />
                <Text variant="body">{user.phone_e164}</Text>
              </View>
            </View>
          )}
          {stats?.school_name && (
            <View style={styles.infoRow}>
              <View style={styles.infoRowIcon}>
                <School size={16} color={colors.textMuted} />
                <Text variant="body">{stats.school_name}</Text>
              </View>
            </View>
          )}
        </View>
      </View>

      {/* Push is native-only; on web the toggle can't request permission or
          register a token, so it would render as a permanently-off dead control. */}
      {Platform.OS !== 'web' && <NotificationSetting />}

      <View style={styles.section}>
        <Overline>About</Overline>
        <View style={styles.infoRows}>
          <Pressable style={styles.infoRow} onPress={() => void openEmail(SUPPORT_EMAIL)} accessibilityRole="link">
            <Text variant="body" link>Contact support</Text>
          </Pressable>
          <Pressable style={styles.infoRow} onPress={() => Linking.openURL(LINKS.privacy)} accessibilityRole="link">
            <Text variant="body" link>Privacy policy</Text>
          </Pressable>
          <Pressable style={styles.infoRow} onPress={() => Linking.openURL(LINKS.terms)} accessibilityRole="link">
            <Text variant="body" link>Terms of service</Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.signOutWrap}>
        <Button label="Sign out" variant="outline" leadingIcon={LogOut} fullWidth onPress={() => void signOutThisDevice()} testID="sign-out-btn" />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Delete account"
          style={styles.deleteAccountRow}
          onPress={() => setDeleteOpen(true)}
          testID="delete-account-btn"
        >
          <Trash2 size={16} color={colors.danger} />
          <Text variant="bodyMedium" color="danger">Delete account</Text>
        </Pressable>
      </View>

      {editNameOpen && <EditNameSheet onClose={() => setEditNameOpen(false)} />}
      {editEmailOpen && <EditEmailSheet onClose={() => setEditEmailOpen(false)} />}
      {deleteOpen && <DeleteAccountSheet onClose={() => setDeleteOpen(false)} />}
    </Screen>
  );
});

export default ProfileTab;

const styles = StyleSheet.create({
  header: {
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  section: {
    marginBottom: spacing.lg,
    gap: spacing.sm,
  },
  infoRows: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.border,
    overflow: 'hidden',
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.base,
    paddingHorizontal: spacing.base,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.border,
  },
  infoRowIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  // Long addresses truncate so "Tap to edit" stays on the row.
  infoRowValue: {
    flexShrink: 1,
    marginRight: spacing.sm,
  },
  infoRowText: {
    flexShrink: 1,
  },
  signOutWrap: {
    marginTop: spacing.sm,
    marginBottom: spacing.xl,
    gap: spacing.base,
  },
  deleteAccountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    minHeight: 44,
  },
});
